import type { DataTableProviderFilterStateValue } from './types';

export const RESERVED_LIST_SEARCH_PARAM_KEYS = [
  'search',
  'q',
  'create',
  'page',
  'perPage',
  'sort'
] as const;

const reservedKeySet = new Set<string>(RESERVED_LIST_SEARCH_PARAM_KEYS);

export function isReservedListSearchParamKey(key: string): boolean {
  return reservedKeySet.has(key);
}

export function serializeFilterValue(
  value: string | Array<string> | number | null | undefined
): string | null {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    const joined = value.map(String).filter((part) => part !== '').join(',');
    return joined === '' ? null : joined;
  }
  const serialized = String(value);
  return serialized === '' ? null : serialized;
}

export function parseFilterQueryParams(
  search: string
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};
  const query = search.startsWith('?') ? search.slice(1) : search;
  if (!query) return filters;

  const params = new URLSearchParams(query);
  params.forEach((value, key) => {
    if (isReservedListSearchParamKey(key)) return;
    filters[key] = value;
  });
  return filters;
}

export function writeDatatableFiltersToUrl(args: {
  filters: Map<string, DataTableProviderFilterStateValue>;
  searchTerm?: string;
  filterKeys?: Iterable<string>;
  pathname?: string;
  search?: string;
}): string {
  const pathname = args.pathname ?? window.location.pathname;
  const params = new URLSearchParams(
    args.search ?? window.location.search
  );
  const knownKeys = new Set<string>([
    ...(args.filterKeys ?? []),
    ...args.filters.keys()
  ]);

  for (const key of Array.from(params.keys())) {
    if (isReservedListSearchParamKey(key)) continue;
    if (knownKeys.has(key) && !args.filters.has(key)) {
      params.delete(key);
    }
  }

  knownKeys.forEach((key) => {
    if (isReservedListSearchParamKey(key)) return;
    const serialized = serializeFilterValue(args.filters.get(key)?.value);
    if (serialized == null) {
      params.delete(key);
    } else {
      params.set(key, serialized);
    }
  });

  const trimmedSearch = args.searchTerm?.trim() ?? '';
  if (trimmedSearch) {
    params.set('search', trimmedSearch);
    params.delete('q');
  } else {
    params.delete('search');
    params.delete('q');
  }

  const query = params.toString();
  const nextUrl = query ? `${pathname}?${query}` : pathname;
  window.history.replaceState({}, document.title, nextUrl);
  return nextUrl;
}
